import { RevenueObservationService } from './../../../revenue/shared/revenue-observation.service';
import { InvoicePaymentHeaderComponent } from './../invoice-payment-header/invoice-payment-header.component';
import { InvoiceUpsertDto } from '../../dto/invoice-upsert.dto';
import { InvoiceLineItemUpsertDto } from '../../dto/invoice-line-item-upsert.dto';
import { CreditCardPaymentInfo } from './../../../revenue/shared/credit-card/credit-card-payment-info.model';
import { PaymentSource } from 'src/app/revenue/shared/payment-source/payment-source.model';
import { PaymentSourceService } from './../../../revenue/shared/payment-source/payment-source.service';
import { BatchService } from './../../../revenue/shared/invoice/batch.service';
import { PaymentTypeDto } from './../../../revenue/shared/payment-types/dto/payment-type.dto';
import { PaymentTypeService } from './../../../revenue/shared/payment-types/payment-type.service';
import { RevenueItem } from './../../../revenue/shared/revenue-items/revenue-item.model';
import { Component, OnInit, Input, ViewChild, OnChanges, Output, EventEmitter } from '@angular/core';
import { initializedPaymentHeaderDto } from '../../dto/payment-header.dto';
import { truncateToDecimals } from 'src/app/shared/util/math-utils';
import { distributeAmountPrecisely } from 'src/app/shared/util/payment-utils';
@Component({
  selector: 'wl-invoice-payment',
  templateUrl: './invoice-payment.component.html',
  styleUrls: ['./invoice-payment.component.less'],
  providers: [PaymentTypeService, PaymentSourceService],
    standalone: false
})
export class InvoicePaymentComponent implements OnInit, OnChanges {
  @Output() addingNewPaymentChange = new EventEmitter<boolean>();
  @Input() invoice: InvoiceUpsertDto;
  @Input() creditCardPayment: CreditCardPaymentInfo;
  @Input() isNewMemberInvoice: boolean;
  @Input() paymentTypeInvalid: boolean;
  // Source of AccountingPackageId per line item. Parent (InvoiceBuilderComponent) must pass its
  // `allRevenueItems` field here so per-line payment types can be locked to the correct AP/gateway.
  @Input() allRevenueItems: RevenueItem[];
  @Output() paymentTypeSelected = new EventEmitter<void>();

  @ViewChild(InvoicePaymentHeaderComponent) paymentHeaderComponent: InvoicePaymentHeaderComponent;

  public get sendAutomaticReceipt() {
    return this.paymentHeaderComponent.sendAutomaticReceipt;
  }

  public paymentTypes: PaymentTypeDto[];
  public paymentSources: PaymentSource[];
  public addingNewPayment = false;

  public flatPaymentIds = new Array<number>();

  constructor(
    public revenueObservationService: RevenueObservationService,
    private _paymentTypeService: PaymentTypeService,
    private _batchService: BatchService,
    private _paymentSourceService: PaymentSourceService
  ) {}

  ngOnInit() {
    this._paymentTypeService.getPaymentTypes().then((data) => {
      this.paymentTypes = data;
    });

    this._batchService.getNextBatchNumber().then((data) => {
      this.invoice.PaymentHeader.Batch_Creation = data;
    });

    this._paymentSourceService.getPaymentSources().then((data) => {
      this.paymentSources = data;
    });
  }

  ngOnChanges() {
    if (!this.invoice.PaymentHeader.PaymentDate) {
      const today = new Date();
      this.invoice.PaymentHeader.PaymentDate = today;
      this.invoice.PaymentHeader.ApplyDate = today;
    }

    if (this.invoice.LineItems) {
      for (let lineItem of this.invoice.LineItems) {
        if (lineItem.Payments) {
          for (let payment of lineItem.Payments) {
            this.flatPaymentIds.push(payment.PaymentID);
          }
        }
      }
    }

    this.initializeLineItemAccountingPackageIds();
  }

  get totalAdjustedAmountDue() {
    let total = 0;
    if (this.invoice.LineItems) {
      this.invoice.LineItems.forEach((lineItem) => {
        total = Math.round((total + lineItem.AdjustedAmountDue) * 100) / 100;
        if (lineItem.TaxLineItems) {
          lineItem.TaxLineItems.forEach((taxLineItem) => {
            total = Math.round((total + taxLineItem.AdjustedAmountDue) * 100) / 100;
          });
        }
      });
    }
    return total;
  }

  get totalPaymentAmount() {
    let total = 0;
    if (this.invoice.LineItems) {
      this.invoice.LineItems.forEach((lineItem) => {
        total += lineItem.PaymentAmountToApply;
        lineItem.TaxLineItems.forEach((taxLineItem) => {
          total += taxLineItem.PaymentAmountToApply;
        });
      });
    }
    return total;
  }

  amountChanged() {
    if (this.invoice.PaymentHeader.Amount > this.totalAdjustedAmountDue) {
      this.invoice.PaymentHeader.Amount = this.totalAdjustedAmountDue;
    }

    this.distributePaymentAmountToLineItems();
  }

  onPaymentTypeSelected() {
    this.applyHeaderPaymentTypeToMatchingLineItems();
    this.paymentTypeSelected.emit();
  }

  isPaymentTypeLockedByLineItem(lineItem: InvoiceLineItemUpsertDto): boolean {
    const headerPaymentType = this.invoice.PaymentHeader._paymentType;
    if (!headerPaymentType) return false;
    const headerApId = headerPaymentType.AccountingPackageId;
    const lineApId = this.resolveLineItemAccountingPackageId(lineItem);
    // Require both sides to have a known, positive AP ID before locking.
    // null/undefined on either side means we cannot confirm a match — leave unlocked.
    if (!headerApId || !lineApId) return false;
    return lineApId === headerApId;
  }

  getPaymentTypesForLineItem(lineItem: InvoiceLineItemUpsertDto): PaymentTypeDto[] {
    if (!this.paymentTypes) return [];
    const apId = this.resolveLineItemAccountingPackageId(lineItem);
    if (!apId) return [];
    return this.paymentTypes.filter((pt) => pt.AccountingPackageId === apId);
  }

  // Returns the AccountingPackageId for a line item. Uses the cached _accountingPackageId if
  // already set; falls back to an on-demand lookup from allRevenueItems so that dropdowns remain
  // functional even when ngOnChanges fires before both inputs arrive.
  private resolveLineItemAccountingPackageId(lineItem: InvoiceLineItemUpsertDto): number | null {
    if (lineItem._accountingPackageId != null) return lineItem._accountingPackageId;
    if (!this.allRevenueItems) return null;
    // eslint-disable-next-line eqeqeq
    const ri = this.allRevenueItems.find((r) => r.ItemNum == lineItem.ItemNum);
    return ri?.AccountingPackageId ?? null;
  }

  // Populates each line item's (and nested tax line item's) transient _accountingPackageId from
  // the parent-supplied allRevenueItems, matched by ItemNum. Safe to call before either input has
  // arrived - it is a no-op until both this.invoice.LineItems and this.allRevenueItems are present.
  private initializeLineItemAccountingPackageIds() {
    if (!this.invoice?.LineItems || !this.allRevenueItems?.length) {
      return;
    }

    this.getAllLineItemsAndTaxLineItems().forEach((item) => {
      // Use == (loose equality) to handle ItemNum arriving as string from some API paths.
      // eslint-disable-next-line eqeqeq
      const revenueItem = this.allRevenueItems.find((ri) => ri.ItemNum == item.ItemNum);
      if (revenueItem != null) {
        item._accountingPackageId = revenueItem.AccountingPackageId;
      }
    });

    this.applyHeaderPaymentTypeToMatchingLineItems();
  }

  // Auto-fills the per-line _paymentType for any line item (or nested tax line item) whose
  // AccountingPackageId matches the header's newly-selected payment type.
  private applyHeaderPaymentTypeToMatchingLineItems() {
    const selectedPaymentType = this.invoice?.PaymentHeader?._paymentType;
    if (!selectedPaymentType) return;
    const headerApId = selectedPaymentType.AccountingPackageId;
    if (!headerApId) return;

    this.getAllLineItemsAndTaxLineItems().forEach((item) => {
      const lineApId = this.resolveLineItemAccountingPackageId(item);
      if (lineApId && lineApId === headerApId) {
        item._paymentType = selectedPaymentType;
      }
    });
  }

  private getAllLineItemsAndTaxLineItems(): InvoiceLineItemUpsertDto[] {
    if (!this.invoice?.LineItems) {
      return [];
    }
    return this.invoice.LineItems.flatMap((lineItem) => [lineItem, ...(lineItem.TaxLineItems ?? [])]);
  }

  private clearNewPaymentAmounts() {
    this.invoice.LineItems.forEach((item) => {
      item.PaymentAmountToApply = 0;
      item.TaxLineItems.forEach((tli) => {
        tli.PaymentAmountToApply = 0;
      });
    });
  }

  private distributePaymentAmountToLineItems() {
    if (!this.invoice.PaymentHeader.Amount) {
      return;
    }

    const allItems = this.invoice.LineItems.flatMap((lineItem) => [lineItem, ...lineItem.TaxLineItems]);

    distributeAmountPrecisely(
      allItems,
      (item) => item.AdjustedAmountDue,
      (item, value) => (item.PaymentAmountToApply = value),
      this.invoice.PaymentHeader.Amount
    );

    this.revenueObservationService.invoiceAmountChanged(this.invoice.InvoiceHeader.InvoiceNum);
  }

  public addPayment() {
    this.revenueObservationService.newPayment();
    this.addingNewPayment = true;
    this.addingNewPaymentChange.emit(true);
  }

  public cancelPayment() {
    this.addingNewPayment = false;
    this.addingNewPaymentChange.emit(false);
    this.invoice.PaymentHeader = initializedPaymentHeaderDto();
    this.clearNewPaymentAmounts();
    this.revenueObservationService.invoiceAmountChanged(this.invoice.InvoiceHeader.InvoiceNum);
  }

  public preventNegativeKey(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  }

  public sanitizePaymentAmount(lineItem: any) {
    if (lineItem.PaymentAmountToApply == null) {
      return;
    }
    // Coerce to number and clamp at 0
    const value = Number(lineItem.PaymentAmountToApply);
    if (isNaN(value) || value < 0) {
      lineItem.PaymentAmountToApply = 0;
    } else {
      lineItem.PaymentAmountToApply = value;
    }
    this.revenueObservationService.invoiceAmountChanged(this.invoice.InvoiceHeader.InvoiceNum);
  }

  public sanitizeTaxPaymentAmount(taxLineItem: any) {
    if (taxLineItem.PaymentAmountToApply == null) {
      return;
    }
    const value = Number(taxLineItem.PaymentAmountToApply);
    if (isNaN(value) || value < 0) {
      taxLineItem.PaymentAmountToApply = 0;
    } else {
      taxLineItem.PaymentAmountToApply = value;
    }
    this.revenueObservationService.invoiceAmountChanged(this.invoice.InvoiceHeader.InvoiceNum);
  }
}
